import type { AdminUserDetail, UserRole } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { displayAccountStatus } from '@/entities/user';
import { formatDateTime } from '@/shared/lib';
import { ContentCard } from '@/shared/ui';

interface UserAccountCardProps {
  readonly user: AdminUserDetail;
}

const ROLE_LABELS: Record<UserRole, string> = { USER: '회원', ADMIN: '관리자' };

const TERM_CLASS = 'text-muted-foreground';

/** 계정 정보 카드 — 이메일·이름·역할·상태·가입일·인증 시각, 차단·탈퇴한 계정이면 그 시각(차단은 사유까지). */
export const UserAccountCard = ({ user }: UserAccountCardProps): ReactElement => {
  const status = displayAccountStatus(user);
  const identityVerified = user.identityVerifiedAt
    ? formatDateTime(user.identityVerifiedAt)
    : user.role === 'ADMIN'
      ? '면제(관리자)'
      : '미인증';

  return (
    <ContentCard title="계정 정보">
      <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-y-2.5 text-sm">
        <dt className={TERM_CLASS}>이메일</dt>
        <dd className="break-all">{user.email}</dd>
        <dt className={TERM_CLASS}>이름</dt>
        <dd>{user.name}</dd>
        <dt className={TERM_CLASS}>역할</dt>
        <dd>{ROLE_LABELS[user.role]}</dd>
        <dt className={TERM_CLASS}>상태</dt>
        <dd className={status.className}>{status.label}</dd>
        <dt className={TERM_CLASS}>가입일</dt>
        <dd>{formatDateTime(user.createdAt)}</dd>
        <dt className={TERM_CLASS}>이메일 인증</dt>
        <dd>{user.emailVerifiedAt ? formatDateTime(user.emailVerifiedAt) : '미인증'}</dd>
        <dt className={TERM_CLASS}>본인인증</dt>
        <dd>{identityVerified}</dd>
        {user.blockedAt ? (
          <>
            <dt className={TERM_CLASS}>차단</dt>
            <dd className="text-destructive">
              {formatDateTime(user.blockedAt)}
              {user.blockedReason ? ` · 사유: ${user.blockedReason}` : ''}
            </dd>
          </>
        ) : null}
        {user.deletedAt ? (
          <>
            <dt className={TERM_CLASS}>탈퇴</dt>
            <dd>{formatDateTime(user.deletedAt)}</dd>
          </>
        ) : null}
      </dl>
    </ContentCard>
  );
};
